import { TFunction, useTranslation } from 'react-i18next';
import styles from './CreateAchievementPage.module.less';
import {
	Col,
	Row,
	Form,
	Checkbox,
	Button,
	Radio,
	Space,
	RadioChangeEvent,
	Spin,
} from 'antd';
import { useSelector } from 'react-redux';
import { projectSelector } from 'redux/selectors/projectSelector';
import FormItem from 'components/form/FormItem';
import FormInput from 'components/form/FormItem/FormInput';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import api, {
	AchievementType,
	CreateAchievementRequest,
	AchievementDefinition,
	AchievementWritePolicy,
} from 'api';
import OmniNotification from 'components/OmniNotification';
import { GetUploadLink, ImageFileModel } from 'utils/fileUtils';
import ImageFileUpload from 'components/ImageFileUpload';
import { ReactNode, useEffect, useState } from 'react';
import Breadcrumb, { IRoutes } from 'components/Breadcrumb/Breadcrumb';

type ParamsProps = {
	projectId: string;
};

type Props = {
	achievement?: AchievementDefinition;
	loading?: boolean;
	children?: ReactNode;
};

interface FormValues {
	name: string;
	title: string;
	description: string;
	unlockedDescription: string;
	unlockedIcon: ImageFileModel | null;
	lockedIcon: ImageFileModel | null;
	isSecret: boolean;
	type: AchievementType;
	target: number;
	bitfieldLength: number;
	serverOnly: boolean;
}

const defaultValues: FormValues = {
	name: '',
	title: '',
	description: '',
	unlockedDescription: '',
	unlockedIcon: null,
	lockedIcon: null,
	isSecret: false,
	type: AchievementType.Simple,
	target: 1,
	bitfieldLength: 1,
	serverOnly: false,
};

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		name: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createAchievementPage.name.label'),
				}),
			)
			.matches(
				/^(?!.*[?;\/:@=&]).*$/,
				t('common.unsafeCharacters', {
					fieldName: t('createAchievementPage.name.label'),
				}),
			)
			.matches(
				/^[\S]*$/,
				t('common.noSpaces', {
					fieldName: t('createAchievementPage.name.label'),
				}),
			)
			.max(
				1000,
				t('validation.maxLength', {
					fieldName: t('createAchievementPage.name.label'),
					count: 1000,
				}),
			),
		title: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createAchievementPage.title.label'),
				}),
			)
			.max(
				30,
				t('validation.maxLength', {
					fieldName: t('createAchievementPage.title.label'),
					count: 30,
				}),
			),
		description: yup
			.string()
			.required(
				t('validation.required', {
					fieldName: t('createAchievementPage.description.label'),
				}),
			)
			.max(
				2000,
				t('validation.maxLength', {
					fieldName: t('createAchievementPage.description.label'),
					count: 2000,
				}),
			),
		type: yup.number(),
		target: yup
			.number()
			.typeError(t('validation.integer'))
			.moreThan(
				0,
				t('validation.positive', {
					fieldName: t('createAchievementPage.type.target.label'),
				}),
			),
		bitfieldLength: yup.number().when('type', {
			is: AchievementType.BitField,
			then: yup
				.number()
				.min(
					yup.ref('target'),
					t('validation.moreThanOrEqual', {
						fieldName: t('createAchievementPage.type.bitfieldLength.label'),
						value: t('createAchievementPage.type.target.label'),
					}),
				)
				.typeError(t('validation.integer')),
		}),
		unlockedDescription: yup
			.string()
			.nullable()
			.max(
				2000,
				t('validation.maxLength', {
					fieldName: t('createAchievementPage.unlockedDescription.label'),
					count: 2000,
				}),
			),
	});

export default function CreateAchievementPage(props: Props | undefined) {
	const { achievement, loading } = props || {};
	const { t } = useTranslation();
	const history = useHistory();
	const projectInfo = useSelector(projectSelector);
	const [routes, setRoutes] = useState<IRoutes[]>([]);
	const { projectId } = useParams<ParamsProps>();
	const [type, setType] = useState(0);
	const validationSchema: any = getValidationSchema(t);
	const pageForm = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues,
	});

	useEffect(() => {
		if (achievement) {
			pageForm.reset(achievement);
			pageForm.setValue('lockedIcon', {
				imageSrc: achievement?.lockedImageURL,
				s3Key: achievement?.lockedIconKey || '',
				fileName: '',
			});
			pageForm.setValue('unlockedIcon', {
				imageSrc: achievement?.unlockedImageURL,
				s3Key: achievement?.unlockedIconKey || '',
				fileName: '',
			});
			pageForm.setValue(
				'serverOnly',
				achievement?.writePolicy === AchievementWritePolicy.Server,
			);
			setType(achievement?.type!);
		}
	}, [achievement]);

	useEffect(() => {
		setRoutes([
			{
				path: `project/${projectInfo?.id}`,
				breadcrumbName: projectInfo?.name as string,
			},
			{
				path: 'game-services',
				breadcrumbName: 'Game Services',
			},
			{
				path: 'achievements',
				breadcrumbName: t('achievementsPage.Header'),
			},
			{
				path: '',
				breadcrumbName: achievement
					? t('editAchievementPage.header')
					: t('createAchievementPage.header'),
			},
		]);
	}, [projectInfo?.id, projectInfo?.name, achievement]);

	const onSubmit = async (values) => {
		try {
			if (achievement?.id) {
				await api.updateAchievement({
					...achievement,
					...values,
					lockedIconKey: values.lockedIcon?.s3Key,
					unlockedIconKey: values.unlockedIcon?.s3Key,
					writePolicy: values.serverOnly
						? AchievementWritePolicy.Server
						: AchievementWritePolicy.Client,
				});
				OmniNotification.success({
					message: t('editAchievementPage.notification.update.message'),
					description: t(
						'editAchievementPage.notification.update.description',
						{
							title: values.title,
						},
					),
				});
			} else {
				const data: CreateAchievementRequest = {
					...values,
					lockedIconKey: values.lockedIcon?.s3Key,
					unlockedIconKey: values.unlockedIcon?.s3Key,
					projectId: Number.parseInt(projectId),
					writePolicy: values.serverOnly
						? AchievementWritePolicy.Server
						: AchievementWritePolicy.Client,
				};
				await api.createAchievement(data);
				OmniNotification.success({
					message: t('createAchievementPage.notification.success.message'),
					description: t(
						'createAchievementPage.notification.success.description',
						{ title: values.title },
					),
				});
			}
		} catch {
			OmniNotification.error({
				message: t('createAchievementPage.notification.error.message'),
				description: t('createAchievementPage.notification.error.description'),
			});
		}

		navigateToAchievementIndex();
	};

	const navigateToAchievementIndex = () => {
		history.push(`/project/${projectId}/game-services/achievements`);
	};

	const onTypeChange = (e: RadioChangeEvent) => {
		pageForm.setValue(
			'type',
			Number.parseInt(e.target.value) as AchievementType,
		);
		pageForm.setValue('target', defaultValues.target);
		pageForm.setValue('bitfieldLength', defaultValues.bitfieldLength);
		setType(e.target.value);
	};

	return (
		<Spin spinning={loading || false}>
			<div className={styles.pageContainer}>
				<Breadcrumb routes={routes} />
				<div className="page-head" data-testid="header">
					<div>
						<h2 className="h2" style={{ wordBreak: 'break-all' }}>
							{achievement
								? t('editAchievementPage.editHeader')
								: t('createAchievementPage.header')}
						</h2>
					</div>
				</div>
				<FormProvider {...pageForm}>
					<Form
						data-testid="achievementForm"
						layout="vertical"
						onFinish={pageForm.handleSubmit(onSubmit)}
					>
						<Row gutter={16}>
							<Col span={16}>
								<Row gutter={24}>
									<Col span={12} data-testid="apiNameGroup">
										<FormInput
											control={pageForm.control}
											translationKey="createAchievementPage.name"
										/>
									</Col>
									<Col span={12} data-testid="titleGroup">
										<FormInput
											control={pageForm.control}
											translationKey="createAchievementPage.title"
										/>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={16} data-testid="descriptionGroup">
								<FormInput
									control={pageForm.control}
									translationKey="createAchievementPage.description"
								/>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={16}>
								<FormInput
									control={pageForm.control}
									translationKey="createAchievementPage.unlockedDescription"
								/>
							</Col>
						</Row>
						<Row className={styles.formRow}>
							<Col span={24}>
								<Row gutter={6}>
									<Col span={12} data-testid="unlockedIcon">
										<div>{t('createAchievementPage.unlockedIcon.label')}</div>
										<Controller
											name="unlockedIcon"
											control={pageForm.control}
											render={({ field, fieldState }) => (
												<ImageFileUpload
													error={fieldState.error}
													className="uploader__box"
													uploadLinkFunction={GetUploadLink}
													field={field as any}
													defaultImageUrl={achievement?.unlockedImageURL}
													acceptedResolution={{
														width: 256,
														height: 256,
													}}
													customSuccessCallback={() => {
														OmniNotification.success({
															message: t(
																'createAchievementPage.notification.uploadUnlockedIconSuccess.message',
															),
															description: t(
																'createAchievementPage.notification.uploadUnlockedIconSuccess.description',
															),
														});
													}}
												/>
											)}
										/>
										<div>
											{t('createAchievementPage.unlockedIcon.aspectRatio')}
										</div>
										<div className="ant-form-item-explain hint">
											{t('createAchievementPage.unlockedIcon.hint')}
										</div>
									</Col>
									{/* <Col span={1} /> */}
									<Col span={12} data-testid="lockedIcon">
										<div>{t('createAchievementPage.lockedIcon.label')}</div>
										<Controller
											name="lockedIcon"
											control={pageForm.control}
											render={({ field, fieldState }) => (
												<ImageFileUpload
													error={fieldState.error}
													className="uploader__box"
													uploadLinkFunction={GetUploadLink}
													field={field as any}
													defaultImageUrl={achievement?.lockedImageURL}
													acceptedResolution={{
														width: 256,
														height: 256,
													}}
													customSuccessCallback={() => {
														OmniNotification.success({
															message: t(
																'createAchievementPage.notification.uploadLockedIconSuccess.message',
															),
															description: t(
																'createAchievementPage.notification.uploadLockedIconSuccess.description',
															),
														});
													}}
												/>
											)}
										/>
										<div>
											{t('createAchievementPage.lockedIcon.aspectRatio')}
										</div>
										<div className="ant-form-item-explain hint">
											{t('createAchievementPage.lockedIcon.hint')}
										</div>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={24}>
								<Row>{t('createAchievementPage.type.label')}</Row>
								<Row className="ant-form-item-explain hint">
									{t('createAchievementPage.type.hint')}
								</Row>
								<Row>
									<Col span={24} style={{ marginLeft: 20, marginTop: 4 }}>
										<Radio.Group
											name="type"
											onChange={onTypeChange}
											value={type}
										>
											<Space direction="vertical">
												<Radio
													data-testid="achievmentTypeSimple"
													value={AchievementType.Simple}
												>
													{t(
														`createAchievementPage.type.achievementTypeEnum.Simple`,
													)}
												</Radio>
												<Radio
													data-testid="achievmentTypeCount"
													value={AchievementType.Count}
												>
													<Row>
														{t(
															`createAchievementPage.type.achievementTypeEnum.Count`,
														)}
													</Row>
													{type === AchievementType.Count && (
														<Row
															gutter={24}
															style={{ marginLeft: 20, width: 245 }}
														>
															<Col span={24} data-testid="countTarget">
																<FormInput
																	control={pageForm.control}
																	type="number"
																	translationKey="createAchievementPage.type.target"
																/>
															</Col>
														</Row>
													)}
												</Radio>
												<Radio
													data-testid="achievmentTypeBitField"
													value={AchievementType.BitField}
												>
													<Row>
														{t(
															`createAchievementPage.type.achievementTypeEnum.BitField`,
														)}
													</Row>
													{type === AchievementType.BitField && (
														<Row
															gutter={24}
															style={{ marginLeft: 20, width: 500 }}
														>
															<Col span={12} data-testid="bitfieldTarget">
																<FormInput
																	control={pageForm.control}
																	translationKey="createAchievementPage.type.target"
																/>
															</Col>
															<Col span={12} data-testid="bitfieldLength">
																<FormInput
																	control={pageForm.control}
																	translationKey="createAchievementPage.type.bitfieldLength"
																/>
															</Col>
														</Row>
													)}
												</Radio>
											</Space>
										</Radio.Group>
									</Col>
								</Row>
							</Col>
						</Row>
						<Row gutter={24} className={styles.formRow}>
							<Col span={16}>
								<FormItem
									name="isSecret"
									label={t('createAchievementPage.isSecret.label')}
									control={pageForm.control}
									render={({ field }) => (
										<Checkbox
											data-testid="isSecretCheckbox"
											checked={field.value}
											{...field}
										>
											{t('createAchievementPage.isSecret.hint')}
										</Checkbox>
									)}
								/>
								{t('createAchievementPage.serverOnly.label')}
								<FormItem
									name="serverOnly"
									control={pageForm.control}
									render={({ field }) => (
										<Checkbox
											data-testid="isServerOnlyCheckbox"
											checked={field.value}
											{...field}
										>
											{t('createAchievementPage.serverOnly.hint')}
										</Checkbox>
									)}
								/>
							</Col>
						</Row>
						<Row gutter={24}>
							<Button
								className="ant-btn color-dark"
								data-testid="cancel"
								onClick={navigateToAchievementIndex}
							>
								{t('common.cancel')}
							</Button>
							<Button
								className="ant-btn-primary"
								data-testid="submit"
								style={{ marginLeft: '8px' }}
								htmlType="submit"
							>
								{achievement
									? t('common.SaveChanges')
									: t('createAchievementPage.createAchievement')}
							</Button>
						</Row>
					</Form>
				</FormProvider>
			</div>
		</Spin>
	);
}
